// Dev-only box-lifecycle fixtures + their dispatcher, split out of `mocks.ts` on the
// `dashboard-mocks` precedent: seven capabilities had a screen and no mock, so `/fleet`
// rendered `<ErrorState/>` and every panel/modal control failed on click; the box's
// jobs-at-once cap write (#6740) rides the same dispatcher and fixture.
//
// THE DETAIL READ IS NOT THE ROSTER ROW, and this module is where that stops
// being a claim. `GET /v1/runners` serves `RunnerRosterResource`;
// `GET /v1/runners/:id` serves `maskBox`, which carries `system_prompt`,
// `owner_kind` and `current_task_id` — the three fields the writes here CHANGE.
// A fixture that re-served the roster row would make the dialog's whole reason
// for point-reading invisible, and the two writes unobservable.
//
// STATEFUL for the same reason every write here re-READS: the dialog refetches the
// detail after each save, so an echo-only mock would show the pre-write box.

import type { BoxDecommissioned } from './box-lifecycle';
import { setMaxSlotsMock } from './box-max-slots-mocks';
import { isPromotePath, promoteMock } from './fleet-promote-mocks';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * `maskBox` as the SERVER sends it — including `mode`, `kind`, `owner_type`, `address`
 * and `presence`, which `BoxDetail` (the client mirror) does not declare. They are here
 * on purpose: a fixture trimmed to the mirror is a fixture that agrees with the client
 * instead of the server — the exact drift `RunnerRosterWireSchema` ended one file over.
 */
const boxes: Record<string, Record<string, unknown>> = {
  run_mock01: {
    id: 'run_mock01',
    status: 'online',
    display_name: 'worker-1',
    mode: 'worker',
    kind: 'byovm',
    owner_type: 'shared',
    owner_kind: 'org',
    owner_id: null,
    system_prompt: 'Prefer small diffs. Never force-push.',
    hostname: 'worker-1',
    runner_version: '0.8.0',
    specs: {
      arch: 'x64',
      cpuModel: 'AMD EPYC 9454',
      cpuCores: 8,
      memBytes: 34_359_738_368,
      diskBytes: 214_748_364_800,
      diskAvailableBytes: 161_061_273_600,
    },
    current_task_id: 'tsk_mock_live',
    last_heartbeat_iso: at('2026-07-17'),
    enrolled_iso: at('2026-06-01'),
    revoked_iso: null,
    drained_iso: null,
    created_iso: at('2026-06-01'),
    ssh_host: '203.0.113.10',
    address: '203.0.113.10',
    health: 'online',
    presence: {
      online: true,
      active_sessions: 1,
      version: '0.8.0',
      stats: { loadAvg1: 0.42, freeMemBytes: 21_474_836_480, freeDiskBytes: 161_061_273_600 },
      beat_at: at('2026-07-17'),
    },
  },
  run_mock02: {
    id: 'run_mock02',
    status: 'offline',
    display_name: 'dev-box',
    mode: null,
    kind: 'byovm',
    owner_type: 'personal',
    owner_kind: 'user',
    owner_id: 'usr_mock01',
    // No briefing: the form's empty case, and the one where Clear is a no-op.
    system_prompt: null,
    hostname: 'dev-box',
    runner_version: '0.7.9',
    specs: null,
    current_task_id: null,
    last_heartbeat_iso: at('2026-07-01'),
    enrolled_iso: at('2026-05-20'),
    revoked_iso: null,
    // Drained by an operator and held out since: the Drained pill and the Undrain story.
    drained_iso: at('2026-07-01'),
    created_iso: at('2026-05-20'),
    ssh_host: null,
    address: null,
    health: 'stale',
    // `null`, not absent — the box holds no live key, which the panel renders.
    presence: null,
  },
};

/**
 * Restore the pristine boxes: the shared reset seam (`./mock-state.ts`). Drain, undrain
 * and the owner / prompt writes edit these rows in place, and the utilization mock reads
 * the drain mark off them (#6559); the stored cap lives beside them
 * (`box-max-slots-mocks.ts`, #6740) and the utilization mock reads it from there.
 */
const PRISTINE_BOXES = structuredClone(boxes);

export function resetBoxLifecycleMocks(): void {
  for (const [id, row] of Object.entries(PRISTINE_BOXES)) boxes[id] = structuredClone(row);
}

/** The 120-char preview + ellipsis every door serves; the prose never comes back. */
const preview = (prompt: string | null): string | null =>
  prompt === null || prompt.length <= 120 ? prompt : `${prompt.slice(0, 120)}…`;

/** One release ledger row per (version, target); the halted one is never installable. */
const releases = [
  { version: '0.8.1', channel: 'stable', target: 'linux-x64', published_at: at('2026-07-16') },
  { version: '0.8.1', channel: 'stable', target: 'linux-arm64', published_at: at('2026-07-16') },
  { version: '0.8.0', channel: 'stable', target: 'linux-x64', published_at: at('2026-07-01') },
];

/**
 * Handle a `/v1/runners/*` LIFECYCLE request, or return `null` so `mockFetch`
 * falls through — which the roster (`GET /v1/runners`) and both enrollment
 * transports still rely on, since those live in `mocks.ts` and are matched there.
 */
export function boxLifecycleMock(path: string, method: string, init?: RequestInit) {
  const bare = path.split('?')[0] ?? path;
  if (!bare.startsWith('/v1/runners/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  const rest = bare.slice('/v1/runners/'.length);
  // Fleet-wide routes share the prefix with the per-box ones and are NOT ids.
  const fleetWide = fleetWideMock(rest, method, init, reject);
  if (fleetWide !== null) return fleetWide;
  // Three `/v1/runners/*` routes stay in `mocks.ts` beside the roster they belong
  // to — both enrollment transports and the per-box revoke. Returning `null`
  // rather than rejecting keeps them reachable: this responder runs BEFORE them,
  // so claiming the whole prefix would break Add-box and Fleet's revoke at once.
  if (rest.startsWith('enrollments')) return null;
  if (rest.endsWith('/revoke')) return null;

  const [id = '', action = ''] = rest.split('/');
  const box = boxes[id];
  // `box.get` is ACCOUNT-FENCED while the roster is not, so an unknown id is a
  // 404 the dialog reads as "not in this org" — never a synthesized box.
  if (box === undefined) return reject();
  // A COPY, as a wire body always is: handing out the live fixture made every
  // re-read return the object the last read already holds, so the query's
  // structural sharing saw no change and the dialog never moved after a write.
  if (action === '' && method === 'GET') return Promise.resolve(structuredClone(box));
  if (action === 'drain' && method === 'POST') return drain(id, box);
  if (action === 'undrain' && method === 'POST') return undrain(id, box);
  if (action === 'decommission' && method === 'POST') return decommission(id, box);
  if (action === 'owner' && method === 'PUT') return setOwner(id, box, init);
  if (action === 'prompt' && method === 'PUT') return setPrompt(id, box, init);
  if (action === 'max-slots' && method === 'PUT') return setMaxSlotsMock(id, box, init);
  return reject();
}

/** The fleet-wide `/v1/runners/*` routes, or null when `rest` is a per-box path. */
function fleetWideMock(
  rest: string,
  method: string,
  init: RequestInit | undefined,
  reject: () => Promise<never>,
): Promise<unknown> | null {
  if (rest === 'releases') {
    return method === 'GET'
      ? Promise.resolve({
          data: releases,
          next_cursor: null,
          view: 'installable',
          truncated: false,
        })
      : reject();
  }
  if (rest === 'update') return method === 'POST' ? rollout(init) : reject();
  // #6735: the staff-only promote request. `releases/<version>/promote` is not a box id.
  if (isPromotePath(rest)) return method === 'POST' ? promoteMock(rest) : reject();
  return null;
}

/** EVACUATE, never kill. A second drain answers `flipped_offline: false` — benign. */
function drain(id: string, box: Record<string, unknown>): Promise<unknown> {
  const wasOnline = box['status'] === 'online';
  box['status'] = 'offline';
  box['health'] = 'stale';
  box['current_task_id'] = null;
  box['drained_iso'] ??= at('2026-07-17');
  return Promise.resolve({
    box_id: id,
    status: 'offline',
    flipped_offline: wasOnline,
    reenqueued_task_ids: wasOnline ? ['tsk_mock_live'] : [],
  });
}

/**
 * Take the machine OUT (#6563): drain, revoke, and the box leaves the utilization read
 * (`mockBoxRevoked`). A revoked box answers 404, as the route does. `?mock_cascade_failed=1`
 * answers the 200 whose token cascade failed (the panel's retry-through-revoke story).
 */
function decommission(id: string, box: Record<string, unknown>): Promise<unknown> {
  if (box['status'] === 'revoked')
    return unhandledMockRoute('POST', `/v1/runners/${id}/decommission`);
  const hadTask = box['current_task_id'] !== null;
  box['status'] = 'revoked';
  box['revoked_iso'] = at('2026-07-17');
  box['current_task_id'] = null;
  const failed = new URLSearchParams(window.location.search).has('mock_cascade_failed');
  const answer: BoxDecommissioned = {
    box_id: id,
    status: 'revoked',
    reenqueued_task_ids: hadTask ? ['tsk_mock_live'] : [],
    cascade_failed: failed ? 'gateway token revoke failed: upstream timeout (mock)' : null,
  };
  return Promise.resolve(answer);
}

/** A decommissioned box: the utilization read drops it, as the server's does. */
export function mockBoxRevoked(id: string): boolean {
  return boxes[id]?.['status'] === 'revoked';
}

/** Clear the mark; never flips the box online — liveness is presence's, not ours. */
function undrain(id: string, box: Record<string, unknown>): Promise<unknown> {
  const cleared = box['drained_iso'] !== null;
  box['drained_iso'] = null;
  return Promise.resolve({ box_id: id, status: box['status'], cleared });
}

/**
 * The drain mark the roster row carries (`fleetBoxesMock`), read off the SAME stateful
 * detail fixture, so an undrain moves the card's pill on the roster re-read too.
 */
export function mockBoxDrainedIso(id: string): string | null {
  const mark = boxes[id]?.['drained_iso'];
  return typeof mark === 'string' ? mark : null;
}

/** The whole ownership PAIR, as the PUT replaces it. */
function setOwner(id: string, box: Record<string, unknown>, init?: RequestInit): Promise<unknown> {
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { owner_kind?: string; owner_id?: string | null })
    : {};
  box['owner_kind'] = body.owner_kind ?? 'org';
  box['owner_id'] = body.owner_id ?? null;
  return Promise.resolve({ box_id: id, owner_kind: box['owner_kind'], owner_id: box['owner_id'] });
}

/** A briefing REPLACES. The echo is the same 120-char preview the read serves. */
function setPrompt(id: string, box: Record<string, unknown>, init?: RequestInit): Promise<unknown> {
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { system_prompt?: string | null })
    : {};
  const next = body.system_prompt ?? null;
  box['system_prompt'] = preview(next);
  return Promise.resolve({ box_id: id, system_prompt: preview(next), cleared: next === null });
}

/**
 * A 202 is NOT an updated fleet. `run_mock_joined` as the pinned version is the
 * story for `started: false` — a rollout was already running and this trigger joined it.
 */
function rollout(init?: RequestInit): Promise<unknown> {
  const body = init?.body
    ? (JSON.parse(String(init.body)) as { channel?: string; version?: string })
    : {};
  const joined = body.version === '0.8.0';
  return Promise.resolve({
    rollout_id: 'rol_mock01',
    channel: body.channel ?? 'stable',
    version: joined ? '0.8.1' : (body.version ?? null),
    started: !joined,
    ...(joined
      ? {
          note: 'a rollout is already running for this channel; joined it — version is the one actually rolling',
        }
      : {}),
  });
}
